"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IkePromiseQueue = exports.IkePromiseWorker = void 0;
const tslib_1 = require("tslib");
class IkePromiseWorker {
    constructor(id) {
        this.failed = 0;
        this.completed = 0;
        this.promise = Promise.resolve();
        this.id = 'worker-' + id;
    }
    run(task) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.promise;
            try {
                const result = yield task.task();
                task.resolve(result);
                this.completed++;
            }
            catch (e) {
                task.reject(e);
                this.failed++;
            }
        });
    }
    stats() {
        return {
            id: this.id,
            completed: this.completed,
            failed: this.failed
        };
    }
}
exports.IkePromiseWorker = IkePromiseWorker;
const NOOP = () => null;
/**
 * Queue for promises, will run at most "concurrency" promises at the same time.
 *
 * Example:
 *  const pq = new IkePromiseQueue(2);
 *  pq.add(() => sleep(200))
 *  await pq.join()
 *
 */
class IkePromiseQueue {
    constructor(log, concurrency) {
        this.log = log;
        /** All workers used by this queue */
        this.workers = [];
        /* Queue of workers ready to be used */
        this.ready = [];
        /** Queue of tasks to be processed */
        this.tasks = [];
        this.concurrency = concurrency;
        for (let i = 0; i < this.concurrency; i++) {
            this.ready[i] = this.workers[i] = new IkePromiseWorker(i);
        }
        this.onIdle = NOOP;
    }
    /**
     *  Join wait for the queue to empty.
     * To make sure all the promises are successful, you must wait for the promises created from .add()
     */
    join() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.isIdle) {
                return Promise.resolve();
            }
            return new Promise(resolve => {
                const existingResolve = this.onIdle;
                this.onIdle = () => { existingResolve(); resolve(); };
            });
        });
    }
    /**
     * Queue a task in the promise queue.
     * If the task fails, the error will be logged.
     */
    add(task) {
        this.addAndReturn(task).catch((error) => {
            // Since the caller did not return this promise,
            // if it rejects then we must handle it here or the app might crash.
            this.log.error({ error }, 'PromiseQueue: caught error from task that did not have a rejection handler');
        });
    }
    /**
     * Queue a task in the promise queue.
     * Will resolve the returned promise when the task has been executed.
     */
    addAndReturn(task) {
        const taskObject = { task, resolve: null, reject: null, promise: null };
        // tslint:disable:promise-must-complete
        const promise = new Promise((resolve, reject) => {
            taskObject.resolve = resolve;
            taskObject.reject = reject;
            this.tasks.push(taskObject);
            if (this.isAvailable) {
                this.run().catch((error) => {
                    // Since this call to run is not returned or awaited,
                    // if it rejects then we must handle it here or the app might crash.
                    this.log.error({ error }, 'PromiseQueue: caught error while calling run() inside addAndReturn');
                });
            }
        });
        taskObject.promise = promise;
        return promise;
    }
    run() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.isIdle) {
                this.onIdle();
                this.onIdle = NOOP;
                return null;
            }
            if (this.tasks.length === 0) {
                return null;
            }
            const task = this.tasks.shift();
            if (task == null) {
                return this.run();
            }
            const worker = this.ready.shift();
            yield worker.run(task);
            this.ready.push(worker);
            this.run().catch((error) => {
                // Since this call to run is not returned or awaited,
                // if it rejects then we must handle it here or the app might crash.
                this.log.error({ error }, 'PromiseQueue: caught error while calling run() recursively inside run()');
            });
        });
    }
    /**
     * Are there any available workers ready
     */
    get isAvailable() {
        return this.ready.length > 0;
    }
    /** Are there any tasks left to do */
    get isIdle() {
        return this.tasks.length === 0 && this.ready.length === this.concurrency;
    }
    /** Get the resolution stats of a worker, the number of resolved and  promises */
    stats() {
        const stats = {};
        for (const worker of this.workers) {
            stats[worker.id] = worker.stats();
        }
        return stats;
    }
}
exports.IkePromiseQueue = IkePromiseQueue;
//# sourceMappingURL=promise.queue.js.map